import React, { useMemo, useState } from 'react';
import { Input, notification } from 'antd';
import { useQueryClient } from 'react-query';
import Modal from '../../../components/Modal';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import {
  ALL_EXCLUDED, ALL_EXCLUDED_RR, PODIUM_TITLES, PODIUM_TITLES_RR,
  SEND_TO_CRM, SENT_TO_CRM_RR, SOURCE
} from '../../../core/constants';
import { IBook } from '../../../section/Common/types/types';
import showNotification from '../../../section/Common/utils/showNotification';
import { useActions } from '../../../context/Actions.context';

const { TextArea } = Input;

interface IProps {
  book: IBook | Record<string, string | number>;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const getPodiumData = (source: string, book: IBook | Record<string, string | number>) => {
  const podiumData = {
    [SOURCE.bookstat]: {
      podiumTitleObject: { amazonasin: book.ASIN },
      podiumTitleType: PODIUM_TITLES,
      excludeType: ALL_EXCLUDED
    },
    [SOURCE['royal-road']]: {
      podiumTitleObject: { fiction_id: book['Fiction Id'] },
      podiumTitleType: PODIUM_TITLES_RR,
      excludeType: ALL_EXCLUDED_RR
    }
  };

  return podiumData[source];
};

const PodiumTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, isActionLoading, setActionLoading } = useActions();
  const queryClient = useQueryClient();
  const podiumData = useMemo(() => getPodiumData(source?.['source'], book), [source, book]);
  const titleForNotification = typeof book?.Title === 'string' ? book.Title : undefined;
  const [notes, setNotes] = useState<string>('');

  const { mutateAsync: mutatePodium } = useUpdateTitle(podiumData?.podiumTitleType);
  const { mutateAsync: mutateExclude } = useUpdateTitle(podiumData?.excludeType);

  const handleClose = () => {
    setNotes('');
    toggleModal();
  };

  const onSend = () => {
    if (!podiumData) {
      notification.error({
        description: 'Unable to determine the target for this action.',
        message: 'Error'
      });
      return;
    }
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    const promise = mutatePodium({ params: podiumData.podiumTitleObject, body });

    showNotification(
      promise,
      'The Title was successfully sent to the Podium Titles tab.',
      'The Title has not been sent to the Podium Titles tab.',
      titleForNotification
    );

    promise.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR) {
        notification.info({
          description: 'Please delete the data from Hubspot.',
          message: ''
        });
      }
    });

    if (id === ALL_EXCLUDED || id === ALL_EXCLUDED_RR) {
      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.finally(() => setActionLoading(false));
      handleClose();
      return;
    }

    mutateExclude({ params: podiumData.podiumTitleObject })
      .then(res => {
        if (res?.status === 200) {
          notification.success({
            description: `Success: Sent ${titleForNotification}`,
            message: 'The Title was successfully sent to the All Excluded tab.'
          });
        }
        queryClient.refetchQueries(`get-row-count${id}`);
        queryClient.refetchQueries(`get-${id}`);
      })
      .catch(() => {
        notification.error({
          description: `Error while sending ${titleForNotification}`,
          message: 'The Title has not been sent to the Podium Titles tab.'
        });
        setActionLoading(false);
      })
      .finally(() => {
        setActionLoading(false);
        handleClose();
      });
  };

  if (!podiumData) {
    return null;
  }

  return (
    <Modal
      okText={'Send To Podium Titles'}
      title='Send To Podium Titles'
      visible={modal}
      onOk={onSend}
      confirmLoading={isActionLoading}
      onCancel={handleClose}
    >
      <div>
        <span>
          Help yourself and others understand why you&apos;re sending this. Leave a note, or skip and
          continue.
        </span>
        <br />
        <br />
        <TextArea
          placeholder='Add your notes here...'
          maxLength={400}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />

        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
          {notes.length} / 400
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(PodiumTitles);